import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, Observable, timer } from 'rxjs';
import { catchError, finalize, map, share, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  OrderCancelledDialogComponent, OrderCancelledDialogData,
} from '../orders/order-cancelled-dialog/order-cancelled-dialog.component';
import { OrderService } from './order.service';

/** Answer of POST /backend/order/<hash>/on_screen/. */
export interface OnScreenAnswer {
  order_hash: string;
  cancelled: boolean;
  closed?: boolean;
  cancel_reason?: string;
  message?: string;
}

/** How often an open Checkout / Split screen tells the backend it is still showing the ticket. */
export const PAYMENT_SCREEN_PING_MS = 60_000;

/** An MQTT cancel waits this long so this POS's own Delete / Cancel has cleared the screen first. */
const MQTT_SETTLE_MS = 1500;

/**
 * ENG-5867: pre-prod #2610-1549 sat 16 minutes on a split screen; another device on the same
 * station started a ticket and the stale-draft sweep cancelled the one being paid. The POS ignored
 * the MQTT update that said so and the cashier only found out when the payment was refused.
 *
 * - `watchPaymentScreen`: Checkout and Split ping the backend every minute while they are open, so
 *   the sweep leaves the ticket alone; the answer also says when it was cancelled.
 * - `onOrderUpdate`: every `/order/<store>/sub` message. A cancel of the ticket on screen closes the
 *   payment screens, starts a fresh ticket and tells the cashier, once.
 */
@Injectable({ providedIn: 'root' })
export class RemoteCancelService {
  private readonly endpoint = `${environment.apiUrl}/backend/order/`;
  /** Hash → the shared ping of every payment screen showing it. */
  private readonly pings = new Map<string, Observable<void>>();
  /** Ticket numbers known for the hashes on a payment screen, for the dialog title. */
  private readonly counters = new Map<string, string>();
  /** Hashes already told about (here or by the ENG-5865 refusal). */
  private readonly noticed = new Set<string>();

  constructor(private http: HttpClient, private orderService: OrderService, private dialog: MatDialog) {
    this.orderService.orderCancelledSubject.subscribe(hash => hash && this.noticed.add(hash));
  }

  /** Runs the ping while subscribed; Checkout and Split showing the same ticket share one. */
  watchPaymentScreen(orderHash: string, orderCounter?: string): Observable<void> {
    if (!orderHash) { return EMPTY; }
    if (orderCounter) { this.counters.set(orderHash, orderCounter); }
    let ping = this.pings.get(orderHash);
    if (!ping) {
      ping = timer(0, PAYMENT_SCREEN_PING_MS).pipe(
        switchMap(() => this.http.post<OnScreenAnswer>(`${this.endpoint}${orderHash}/on_screen/`, {}).pipe(
          catchError(() => EMPTY),
        )),
        tap(answer => {
          if (answer?.cancelled) { this.notice(orderHash, answer.message, answer.cancel_reason); }
        }),
        map(() => undefined),
        finalize(() => {
          this.pings.delete(orderHash);
          this.counters.delete(orderHash);
        }),
        share(),
      );
      this.pings.set(orderHash, ping);
    }
    return ping;
  }

  /** `bot_order` of an `/order/<store>/sub` message. */
  onOrderUpdate(botOrder: any): void {
    const hash = botOrder?.order_hash;
    if (!botOrder?.is_cancelled || !hash || !this.isOnScreen(hash) || this.noticed.has(hash)) { return; }
    setTimeout(() => this.notice(hash, null, botOrder.cancel_reason, botOrder.order_counter), MQTT_SETTLE_MS);
  }

  private isOnScreen(orderHash: string): boolean {
    return localStorage.getItem('order_hash') === orderHash || this.pings.has(orderHash);
  }

  private notice(orderHash: string, message: string | null, reason?: string, orderCounter?: string): void {
    if (this.noticed.has(orderHash) || this.orderService.wasCancelledHere(orderHash) || !this.isOnScreen(orderHash)) {
      return;
    }
    this.noticed.add(orderHash);
    const counter = orderCounter || this.counters.get(orderHash);
    // A payment screen can be for an Open Orders order while the POS keeps its own ticket:
    // only a cancel of the POS ticket starts a fresh one.
    const posTicket = localStorage.getItem('order_hash') === orderHash;
    const data: OrderCancelledDialogData = {
      title: counter ? `Ticket #${counter} was cancelled` : 'Ticket cancelled',
      message: message || `This ticket was cancelled${reason ? ` (${reason})` : ''}. It can't take payments.`,
      newTicket: posTicket,
    };
    // The same clean-up as the ENG-5865 refusal: payment screens close, the ticket leaves the POS.
    this.orderService.isCashCLose.next(true);
    this.orderService.orderCancelledSubject.next(orderHash);
    if (posTicket) {
      localStorage.removeItem('order_hash');
      this.orderService.orderDoneSubject.next();
    }
    this.dialog.open<OrderCancelledDialogComponent, OrderCancelledDialogData>(OrderCancelledDialogComponent, {
      data,
      width: '440px',
      maxWidth: '94vw',
      disableClose: true,
      autoFocus: 'dialog',
      panelClass: 'order-cancelled-dialog-panel',
    });
  }
}
